// Add charge sheet — slides up like SettleSheet.
// Posts to /v2/provider/billing/charges. Genuinely a different verb from
// settle (it grows the ledger), kept as a separate sheet rather than a
// method on the SettleSheet selector.

import { useState } from 'react';
import { useCaptureCharges } from '@features/billing/store/hooks';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Slide,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

const CHARGE_TYPE_OPTIONS = [
  { value: 'no_show', label: 'No-show fee', code: '' },
  { value: 'late_cancel', label: 'Late-cancel fee', code: '' },
  { value: 'paper_form', label: 'Paper-form fee', code: '' },
  { value: 'cpt_99214', label: 'CPT 99214 (OON visit)', code: 'CPT:99214' },
  { value: 'other', label: 'Other', code: '' },
] as const;

type ChargeKind = (typeof CHARGE_TYPE_OPTIONS)[number]['value'];

interface Props {
  patientId: string;
  encounterId?: string | null;
  onClose: () => void;
}

export const AddChargeSheet = ({ patientId, encounterId, onClose }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { payment } = useEventTracking();
  const { captureCharges, isLoading } = useCaptureCharges();
  const [amountStr, setAmountStr] = useState('');
  const [kind, setKind] = useState<ChargeKind>('no_show');
  const [description, setDescription] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const cents = Math.round((Number(amountStr) || 0) * 100);
  const canSubmit = !isLoading && cents > 0;

  const handleSubmit = async () => {
    setSubmitError(null);
    const option = CHARGE_TYPE_OPTIONS.find((o) => o.value === kind);
    const result = await captureCharges({
      patient_id: patientId,
      encounter_id: encounterId ?? null,
      items: [
        {
          charge_amount_cents: cents,
          billing_code: option?.code || undefined,
          description: description || option?.label || 'Patient charge',
          units: 1,
        },
      ],
    });
    if (!result.success) {
      const err = result.error;
      setSubmitError(err instanceof Error ? err.message : 'Failed to add charge');
      return;
    }
    payment.chargesCaptured({
      encounter_id: encounterId ?? undefined,
      item_count: 1,
      total_charge_cents: cents,
    });
    enqueueSnackbar(`Added ${dollars(cents)} charge`, { variant: 'success' });
    onClose();
  };

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          top: 'auto',
          bottom: 0,
          maxHeight: '80%',
          bgcolor: ELIG_TOKENS.card,
          borderTop: `1px solid ${ELIG_TOKENS.lineStrong}`,
          boxShadow: '0 -8px 24px rgba(28,30,133,0.10)',
          display: 'flex',
          flexDirection: 'column',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${ELIG_TOKENS.line}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ELIG_TOKENS.midnight }}>
            Add charge
          </Typography>
          <Button
            onClick={onClose}
            size="small"
            sx={{ color: ELIG_TOKENS.inkSoft, textTransform: 'none' }}
          >
            Cancel
          </Button>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          <Box sx={{ mb: 1.5 }}>
            <TextField
              label="Amount"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              fullWidth
              size="small"
              autoFocus
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Box>
          <Box sx={{ mb: 1.5 }}>
            <TextField
              select
              label="Type"
              value={kind}
              onChange={(e) => setKind(e.target.value as ChargeKind)}
              fullWidth
              size="small"
            >
              {CHARGE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ mb: 1.5 }}>
            <TextField
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Box>

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${ELIG_TOKENS.line}`,
            bgcolor: ELIG_TOKENS.bg,
            px: 3,
            py: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{
              bgcolor: ELIG_TOKENS.midnight,
              color: '#fff',
              '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.9)' },
              minWidth: 140,
            }}
          >
            {isLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Add charge'}
          </Button>
        </Box>
      </Box>
    </Slide>
  );
};

const dollars = (cents: number): string => {
  const abs = Math.abs(cents);
  if (abs % 100 === 0) return `$${abs / 100}`;
  return `$${(abs / 100).toFixed(2)}`;
};
